import { useState, useEffect, useRef, useCallback } from 'react';
import {
  isConnectorAvailable,
  onLocalCoreInitFailed,
  onLocalCoreResetComplete,
  resetLocalDb,
} from '../lib/connector';

const GOLD = '#C9A84C';

const BODY_TEXT =
  "Maestro couldn't open your local library database. Streaming from Qobuz still works. To restore local library access you can reset the database — this will require re-scanning your music.";

export default function LocalCoreInitFailedPrompt() {
  const [visible, setVisible] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const hasSetup = useRef(false);
  const unlistenRefs = useRef([]);

  const dismiss = useCallback(() => {
    setVisible(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (hasSetup.current || !isConnectorAvailable()) return;
    hasSetup.current = true;

    const setup = async () => {
      unlistenRefs.current.push(
        await onLocalCoreInitFailed(() => {
          setVisible(true);
          setError(null);
        }),
      );
      unlistenRefs.current.push(
        await onLocalCoreResetComplete(() => {
          setResetting(false);
          setVisible(false);
          setError(null);
        }),
      );
    };

    setup().catch((err) => {
      console.error('[LocalCoreInitFailed] listen failed:', err);
    });

    return () => {
      unlistenRefs.current.forEach((fn) => {
        if (typeof fn === 'function') fn();
      });
      unlistenRefs.current = [];
    };
  }, []);

  const handleReset = useCallback(async () => {
    setResetting(true);
    setError(null);
    try {
      await resetLocalDb();
    } catch (err) {
      console.error('[LocalCoreInitFailed] reset failed:', err);
      setResetting(false);
      setError(err?.message || String(err));
    }
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99997,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: '#1a1a1a', borderRadius: 16, padding: '32px 36px',
        maxWidth: 520, width: '90%', position: 'relative',
        border: `1px solid ${GOLD}33`, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '0 0 12px' }}>
          Library database error
        </h2>

        <p style={{ color: '#aaa', fontSize: 14, lineHeight: 1.6, textAlign: 'center', margin: '0 0 16px' }}>
          {BODY_TEXT}
        </p>

        {error && (
          <p style={{ color: '#e88', fontSize: 13, textAlign: 'center', margin: '0 0 16px' }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleReset}
            disabled={resetting}
            style={{
              background: GOLD, color: '#000', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontSize: 14,
              fontWeight: 600, cursor: resetting ? 'wait' : 'pointer',
              opacity: resetting ? 0.7 : 1,
            }}
          >
            {resetting ? 'Resetting…' : 'Reset Database'}
          </button>
          <button
            onClick={dismiss}
            disabled={resetting}
            style={{
              background: 'transparent', color: GOLD,
              border: `1px solid ${GOLD}`, borderRadius: 8,
              padding: '10px 20px', fontSize: 14,
              fontWeight: 600, cursor: resetting ? 'not-allowed' : 'pointer',
              opacity: resetting ? 0.5 : 1,
            }}
          >
            Continue without local library
          </button>
        </div>
      </div>
    </div>
  );
}
